import { useEffect, useState } from 'react';

import { ENROLMENT, useEnrolmentSession } from '@/lib/enrolmentSession';

import { RECORDED_COPY } from '../copy';

/**
 * The live region that carries the boot wait to a screen reader [03-us-kad-6i AC-11; eval row **O1**].
 *
 * ⛔ **THE INTERIM BOOT STATE DRAWS ONE ELEMENT AND IT ANNOUNCES NOTHING.** `Spinner` is
 * `role="presentation"` [`Spinner.tsx`], so it is invisible to assistive technology by construction: the
 * 2026-09-18 ruling that put it on the screen changed WHO is served, not how many — a sighted staffer gained
 * a signal, and a screen-reader user still has exactly one, this [decision `0307`].
 *
 * ## Three things about it are load-bearing, and not one of them is an attribute
 *
 * ⛔ **0. It carries NO `aria-busy`, which is the one attribute the precedents have and this must not.**
 * They hardcode it on a region that UNMOUNTS; this one goes true→false while mounted, and the flag and the
 * sentence land in DIFFERENT commits — the attribute is derived during render, the text is written from a
 * passive effect. So on the verdict there is a commit reading `aria-busy="false"` with *"Checking this
 * device…"* still inside it, which is exactly the moment an assistive technology that buffered the
 * insertion flushes it — over the candidate's first screen. ⭐ **The region is `sr-only` with one text node
 * and nothing partial to shield, so the attribute bought nothing and cost that.**
 *
 * ⭐ **1. It lives in `App`, ABOVE the enrolment fork — `EnrolmentFlow` is not high enough.** `App` returns
 * `<EnrolmentFlow>` only while the device is unenrolled, so the moment the verdict is `enrolled` — the
 * story's own demonstration, eval row **B1** — that whole subtree unmounts. On a healthy network the probe
 * is sub-second, and a `polite` announcement queued behind a screen reader's current utterance is discarded
 * when the region carrying it disappears. ⛔ **So the region outlives the state swap**, and ⭐ **its text is
 * CLEARED when the verdict lands** — the region stays, the sentence does not, and nothing announces a stale
 * *"checking…"* over a candidate's first screen.
 *
 * ⭐ **2. The region is mounted EMPTY on the first commit and its text is written on a LATER update.** ⛔ An
 * ARIA live region announces a CHANGE to a region already in the accessibility tree; it does not announce
 * its own initial content. ⚠️ **And this state is the FIRST render, not a transition into one** — the store
 * initialises to it [`enrolmentSession.ts`] — so a region rendered with its sentence already inside it on
 * that first commit is initial content, and a screen reader says nothing while every attribute passes
 * review. ⭐ **That is what the effect below is for**: React commits the empty region, the browser paints it,
 * and only then does the text arrive as a change. ⛔ **`ConnectingScreen` and `CheckingScreen` are NOT
 * precedents for this half** — they work because both are entered by a transition.
 */
export function EnrolmentAnnouncement() {
  const checking = useEnrolmentSession((state) => state.enrolment === ENROLMENT.checking);
  const [announcement, setAnnouncement] = useState('');

  useEffect(() => {
    /*
     * ⭐ **The whole mechanism is this ORDERING, not the markup below** [`0307` § *The registration
     * order*]. Writing the sentence during render would put it in the region's initial content, which is
     * silent; writing it from an effect makes it the region's first CHANGE, which is spoken.
     *
     * ⛔ **So the suppression below IS the criterion.** `set-state-in-effect` is right about the general
     * case and wrong about this one: the cascading render it objects to is the second commit, and the
     * second commit is the announcement. Obeying it here would leave every attribute in place and nothing
     * spoken [eval row **O1**].
     */
    // eslint-disable-next-line react-hooks/set-state-in-effect -- 03-us-kad-6i AC-11; decision `0307` § *The registration order*
    setAnnouncement(checking ? RECORDED_COPY.checkingEnrolment : '');
  }, [checking]);

  return (
    <p aria-live="polite" className="sr-only">
      {announcement}
    </p>
  );
}
